import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import miloszPhoto from "../../assets/milosz.jpg";
import dominikaPhoto from "../../assets/dominika.jpg";
import SectionLabel from "../ui/SectionLabel";
import SplitHeading from "../ui/SplitHeading";
import { useLang } from "../../context/LangContext";
import { c } from "../../content";

const PHOTOS = {
  milosz: miloszPhoto,
  dominika: dominikaPhoto,
};

function TeamRow({ member, lang, index, reverse }) {
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const [hovered, setHovered] = useState(false);
  const rowRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: rowRef, offset: ["start end", "end start"] });
  const rawY = useTransform(scrollYProgress, [0, 1], [-18, 18]);
  const photoY = useSpring(rawY, { stiffness: 60, damping: 20 });

  const photo = PHOTOS[member.id];

  return (
    <div
      ref={rowRef}
      className={`reveal flex flex-col ${reverse ? "lg:flex-row-reverse" : "lg:flex-row"} items-center gap-10 lg:gap-16`}
    >
      {/* ── Photo ── */}
      <div className="w-full shrink-0" style={{ maxWidth: 380 }}>
        <div
          className="relative overflow-hidden mx-auto"
          style={{
            borderRadius: 14,
            border: "1px solid rgba(200,121,65,0.15)",
            aspectRatio: "4 / 5",
            maxWidth: 380,
            cursor: "default",
          }}
          onMouseMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            setMousePos({ x: e.clientX - r.left, y: e.clientY - r.top });
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <div
            aria-hidden="true"
            style={{
              position: "absolute", inset: 0,
              background: `radial-gradient(circle at ${mousePos.x}px ${mousePos.y}px, rgba(200,121,65,0.18) 0%, transparent 55%)`,
              opacity: hovered ? 1 : 0,
              transition: "opacity 0.4s ease",
              pointerEvents: "none", zIndex: 2,
            }}
          />
          <motion.img
            src={photo}
            alt={member.name}
            loading="lazy"
            style={{ y: photoY, scale: 1.07 }}
            className="w-full h-full object-cover object-top"
            draggable="false"
          />
        </div>
      </div>

      {/* ── Text ── */}
      <div className={`w-full text-center ${reverse ? "lg:text-right" : "lg:text-left"}`}>
        <span
          aria-hidden="true"
          className="font-display font-light block"
          style={{ fontSize: "clamp(2.4rem, 4vw, 3.2rem)", color: "rgba(200,121,65,0.16)", lineHeight: 1 }}
        >
          {String(index + 1).padStart(2, "0")}
        </span>

        <p className="font-display text-2xl lg:text-3xl font-light text-white-warm leading-tight mt-3">
          {member.name}
        </p>
        <p className="font-mono text-[11px] tracking-[0.28em] uppercase mt-2.5" style={{ color: "rgba(200,121,65,0.75)" }}>
          {member.role[lang]}
        </p>

        <p
          className={`font-body text-base lg:text-lg leading-[1.8] mt-5 ${reverse ? "lg:ml-auto" : ""}`}
          style={{ color: "rgba(242,232,216,0.62)", maxWidth: "46ch" }}
        >
          {member.desc[lang]}
        </p>
      </div>
    </div>
  );
}

export default function About() {
  const { lang } = useLang();
  const sectionRef = useRef(null);

  return (
    <section
      ref={sectionRef}
      id="o-nas"
      className="section-pad relative overflow-hidden"
      style={{ background: "#100F0C" }}
      data-testid="about-section"
    >
      <div className="pointer-events-none absolute -top-60 -right-40 w-[700px] h-[700px] rounded-full" style={{ background: "radial-gradient(circle, rgba(200,121,65,0.04), rgba(200,121,65,0) 65%)", filter: "blur(80px)" }} />
      <div className="pointer-events-none absolute -bottom-40 -left-20 w-[500px] h-[500px] rounded-full" style={{ background: "radial-gradient(circle, rgba(124,196,216,0.025), rgba(124,196,216,0) 65%)", filter: "blur(80px)" }} />

      <div className="max-w-7xl mx-auto relative">

        {/* ── Header ── */}
        <div className="reveal max-w-3xl mb-16 lg:mb-20">
          <div className="mb-5">
            <SectionLabel>{c.about.label[lang]}</SectionLabel>
          </div>

          <SplitHeading
            line1={c.about.heading1[lang]}
            line2={<em className="italic text-amber">{c.about.ice[lang]}</em>}
            className="font-display font-light leading-[1.05] text-white-warm"
            style={{ fontSize: "clamp(2.2rem, 4.4vw, 3.6rem)" }}
            data-testid="about-title"
          />

          <p className="font-body text-lg leading-[1.85] mt-7" style={{ color: "rgba(242,232,216,0.62)", maxWidth: "54ch" }}>
            {c.about.intro1[lang]}
          </p>
          <p className="font-body text-lg leading-[1.85] mt-4" style={{ color: "rgba(242,232,216,0.62)", maxWidth: "54ch" }}>
            {c.about.intro2[lang]}
          </p>
        </div>

        {/* ── Team rows ── */}
        <div className="flex flex-col gap-20 lg:gap-28 max-w-4xl mx-auto">
          {c.about.team.map((member, i) => (
            <TeamRow key={member.id} member={member} lang={lang} index={i} reverse={i % 2 === 1} />
          ))}
        </div>

        {/* ── Closing + CTA ── */}
        <div className="reveal d3 mt-16 lg:mt-20 text-center">
          <p className="font-body text-lg leading-[1.85] mx-auto" style={{ color: "rgba(242,232,216,0.62)", maxWidth: "50ch" }}>
            {c.about.closing[lang]}
          </p>

          <p className="font-mono text-[10px] tracking-[0.22em] uppercase mt-8" style={{ color: "rgba(242,232,216,0.22)" }}>
            {c.about.location[lang]}
          </p>

          <a href="#kontakt" className="btn-ghost mt-8 inline-flex" data-testid="about-cta">
            {c.about.cta[lang]}
          </a>
        </div>
      </div>
    </section>
  );
}